import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Add } from '@mui/icons-material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import PageTabNavigation from 'src/shared/components/navigations/pageTabNavigation';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpPageSearchFilter } from '../models/helpPageSearchFilter';
import { HelpPageCategory } from '../models/helpPageCategory';
import { HelpPageModule } from '../models/helpPageModule';
import { HelpPageStatusEnum } from '../models/helpPageStatus.enum';
import { getAllHelpPageCategories } from '../services/helpPageCategoryService';
import { getAllHelpPageModules } from '../services/helpPageModuleService';
import { HelpArticlesDataGrid } from '../components/helpArticlesDataGrid';
import { helpArticlesPageTabs } from '../functions/helpArticlesPageTabs';
import { getCategoryIdsWithChildren, getCategoryOptions } from '../functions/getCategoryOptions';

export const HelpArticlesOverviewPage = () => {
  const { t } = useTranslation(['helpArticles']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const [categories, setCategories] = useState<HelpPageCategory[]>([]);
  const [modules, setModules] = useState<HelpPageModule[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [loadingModules, setLoadingModules] = useState<boolean>(true);
  const [refetchDataGrid, setRefetchDataGrid] = useState<boolean>(false);
  const [statusFilterKey, setStatusFilterKey] = useState<number>(0);
  const [searchFilter, setSearchFilter] = useState<HelpPageSearchFilter>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    sortCol: 'updatedAt',
    sortDir: 'desc',
    filter: '',
  });

  // Kategorierna och modulerna hämtas var för sig, så att ett fel bara lämnar sitt eget filter tomt.
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const result = await getAllHelpPageCategories();
        if (isMounted()) setCategories(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoadingCategories(false);
      }
    };
    const loadModules = async () => {
      try {
        const result = await getAllHelpPageModules();
        if (isMounted()) setModules(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoadingModules(false);
      }
    };
    loadCategories();
    loadModules();
  }, [isMounted]);

  const categoryOptions = useMemo(() => getCategoryOptions(categories), [categories]);

  // Arkiverad går bara att välja när arkiverade visas, så att listan aldrig visar dem medan rutan säger att de är dolda.
  const statusOptions = [
    { id: HelpPageStatusEnum.Draft, name: t('helpArticles:status.draft') },
    { id: HelpPageStatusEnum.Published, name: t('helpArticles:status.published') },
    ...(searchFilter.includeArchived ? [{ id: HelpPageStatusEnum.Archived, name: t('helpArticles:status.archived') }] : []),
  ];

  const handleIncludeArchivedChange = (includeArchived: boolean) => {
    const clearStatus = !includeArchived && searchFilter.status === HelpPageStatusEnum.Archived;
    // Statusfiltret håller sitt val själv och går inte att tömma via value, så det byggs om när valet tas bort.
    if (clearStatus) setStatusFilterKey((prev) => prev + 1);
    setSearchFilter((prev) => ({ ...prev, includeArchived, status: clearStatus ? undefined : prev.status, pageIndex: 0 }));
  };

  return (
    <GridWrapper>
      <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <MainContentHeader title={t('helpArticles:title')} />
      </Grid>
      <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => navigate(RoutesEnum.HelpArticleCreate)}
          variant='contained'
          color='primary'
          startIcon={<Add fontSize='small' />}
          disableElevation
        >
          {t('helpArticles:createArticle')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <PageTabNavigation pageTabs={helpArticlesPageTabs(t)} />
          <PageFilterSection
            leftContent={
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <SearchField
                  placeholder={t('helpArticles:searchPlaceholder')}
                  initialValue={searchFilter.filter || ''}
                  onSearch={(newSearchString) => setSearchFilter((prev) => ({ ...prev, filter: newSearchString, pageIndex: 0 }))}
                />
                {/* En huvudkategori visar även sidorna i sina underkategorier, som kategorisidan. */}
                <AutocompleteDropdown<{ id: number; name: string }>
                  componentWidth='12rem'
                  keyProp='id'
                  nameProp='name'
                  label={t('helpArticles:filter.category')}
                  options={categoryOptions}
                  loading={loadingCategories}
                  callback={(selected) =>
                    setSearchFilter((prev) => ({
                      ...prev,
                      categoryIds: selected ? getCategoryIdsWithChildren(categories, selected.id) : undefined,
                      pageIndex: 0,
                    }))
                  }
                />
                <AutocompleteDropdown<HelpPageModule>
                  componentWidth='12rem'
                  keyProp='id'
                  nameProp='name'
                  label={t('helpArticles:filter.module')}
                  options={modules}
                  loading={loadingModules}
                  callback={(selected) => setSearchFilter((prev) => ({ ...prev, moduleId: selected?.id, pageIndex: 0 }))}
                />
                <AutocompleteDropdown<{ id: HelpPageStatusEnum; name: string }>
                  key={statusFilterKey}
                  componentWidth='12rem'
                  keyProp='id'
                  nameProp='name'
                  label={t('helpArticles:filter.status')}
                  options={statusOptions}
                  callback={(selected) => setSearchFilter((prev) => ({ ...prev, status: selected?.id, pageIndex: 0 }))}
                />
                <FormControlLabel
                  control={
                    <Checkbox size='small' checked={!!searchFilter.includeArchived} onChange={(e) => handleIncludeArchivedChange(e.target.checked)} />
                  }
                  label={t('helpArticles:filter.showArchived')}
                />
              </Box>
            }
          />
          <Box className='section-inset'>
            <HelpArticlesDataGrid
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) => setSearchFilter((prev) => ({ ...prev, ...newFilter }))}
              categories={categories}
              modules={modules}
              refetchDataGrid={refetchDataGrid}
              setRefetchDataGrid={setRefetchDataGrid}
              onSelectHelpPage={(helpPage) => navigate(`${RoutesEnum.HelpArticleEdit}/${helpPage.id}`)}
            />
          </Box>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
